import { useState } from 'react';

import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bot,
  Building,
  Building2,
  CheckCircle,
  CircleAlert,
  ClipboardCheck,
  Clock,
  Cloud,
  Database,
  Fingerprint,
  FolderKey,
  Grid3X3,
  Heart,
  Key,
  KeyRound,
  Layers,
  ListChecks,
  Lock,
  Mail,
  RefreshCw,
  Settings,
  Shield,
  ShieldCheck,
  Timer,
  TrendingUp,
  UserPlus,
  Users,
  UsersRound,
  XCircle,
} from 'lucide-react';

import iamCertificationStages from '@src/assets/screenshots/iam-certification-stages.webp';
import iamCertificationTasks from '@src/assets/screenshots/iam-certification-tasks.webp';
import iamCertifications from '@src/assets/screenshots/iam-certifications.webp';
import iamEntitlements from '@src/assets/screenshots/iam-entitlements.webp';
import iamPeopleEntitlement from '@src/assets/screenshots/iam-people-entitlement.webp';
import iamPeople from '@src/assets/screenshots/iam-people.webp';
import iamRbac from '@src/assets/screenshots/iam-rbac.webp';
import iamRequests from '@src/assets/screenshots/iam-requests.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const BG_COLOR = '#F8FAFC';

// Pain points
const painPoints = [
  {
    icon: XCircle,
    problem: 'Access Sprawl',
    description: 'Users accumulate permissions over time through role changes and project assignments, creating excessive access that violates least privilege.',
    solution: 'Automated access certification campaigns review all entitlements and revoke unnecessary access systematically.',
  },
  {
    icon: Timer,
    problem: 'Manual Provisioning',
    description: 'IT teams spend hours manually creating accounts and assigning permissions for new hires, leading to delays and errors.',
    solution: 'Automated joiner-mover-leaver workflows provision access instantly based on role, department, and location.',
  },
  {
    icon: AlertTriangle,
    problem: 'Orphaned Accounts',
    description: 'Ex-employees retain active accounts and access because offboarding processes are incomplete or delayed.',
    solution: 'Real-time HR integration triggers immediate deprovisioning when employment status changes.',
  },
  {
    icon: Layers,
    problem: 'Compliance Gaps',
    description: 'Auditors find segregation of duties violations and lack of access evidence, risking regulatory penalties.',
    solution: 'SOD Matrix enforcement, audit trails, and compliance reports ready for SOX, HIPAA, and SOC 2 audits.',
  },
];

// IAM capabilities from screenshots - 12 categories
const igaCapabilities = [
  { name: 'Account Types', icon: Key, description: 'Define and manage identity account types across systems' },
  { name: 'Certifications', icon: BadgeCheck, description: 'Access certification campaigns with multi-stage reviews' },
  { name: 'Entitlements', icon: FolderKey, description: 'Granular permission and entitlement management' },
  { name: 'Identities', icon: Fingerprint, description: '1,000+ identities synced from AD, Okta, Salesforce, Entra ID' },
  { name: 'RBAC', icon: ShieldCheck, description: 'Role-based access control with NICE framework alignment' },
  { name: 'Requests', icon: ClipboardCheck, description: 'Self-service access requests with approval workflows' },
  { name: 'SSO', icon: KeyRound, description: 'Single sign-on configuration and federation management' },
  { name: 'People', icon: Users, description: 'All personnel with lifecycle status tracking' },
  { name: 'Invitations', icon: Mail, description: 'Guest and external user invitation management' },
  { name: 'SOD Matrix', icon: Grid3X3, description: 'Segregation of duties conflict detection and prevention' },
  { name: 'System Groups', icon: UsersRound, description: 'Manage groups across connected identity systems' },
  { name: 'System Users', icon: Bot, description: 'Service accounts and non-human identity management' },
];

// Identity sources from screenshots
const identitySources = [
  { name: 'Active Directory', color: '#0078d4', identities: '412' },
  { name: 'Salesforce', color: '#00a1e0', identities: '245' },
  { name: 'Okta', color: '#007dc1', identities: '198' },
  { name: 'Entra ID', color: '#0078d4', identities: '157' },
];

// Campaign scope types from screenshots
const campaignScopes = [
  {
    name: 'Entity / Sub-Entity',
    icon: Building,
    description: 'Review access by Organization, Department, Team, or Project',
  },
  {
    name: 'Assets',
    icon: Database,
    description: 'Review access for connected assets',
  },
  {
    name: 'People',
    icon: Users,
    description: 'Create a review campaign for specific users',
  },
];

// People statuses from screenshots
const peopleStatuses = [
  { name: 'Active', color: '#10b981', count: 342 },
  { name: 'Inactive', color: '#6b7280', count: 87 },
  { name: 'Retired', color: '#8b5cf6', count: 24 },
  { name: 'Suspended', color: '#f59e0b', count: 18 },
  { name: 'Deleted', color: '#ef4444', count: 12 },
  { name: 'Banned', color: '#dc2626', count: 2 },
];

export function IAMPage() {
  const [activeTab, setActiveTab] = useState('all');
  const onNavigateToTop = useNavigateToTop();

  const tabs = [
    { id: 'all', label: 'All Capabilities', count: 12 },
    { id: 'governance', label: 'Governance', count: 4 },
    { id: 'lifecycle', label: 'Lifecycle', count: 4 },
    { id: 'access', label: 'Access Control', count: 4 },
  ];

  const capabilityGroups = {
    all: igaCapabilities,
    governance: igaCapabilities.filter((c) => ['Certifications', 'SOD Matrix', 'Entitlements', 'RBAC'].includes(c.name)),
    lifecycle: igaCapabilities.filter((c) => ['People', 'Identities', 'Invitations', 'System Users'].includes(c.name)),
    access: igaCapabilities.filter((c) => ['Account Types', 'Requests', 'SSO', 'System Groups'].includes(c.name)),
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 50%, #bfdbfe 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Users className="w-8 h-8" style={{ color: `${BOSSS_BLUE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Shield className="w-6 h-6" style={{ color: `${BOSSS_BLUE}40` }} />
          </motion.div>

          <motion.div
            animate={{ y: [0, -20, 0] }}
            className="absolute top-40 right-1/4"
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Key className="w-5 h-5" style={{ color: `${BOSSS_BLUE}45` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Users className="w-4 h-4 mr-2" />
                Identity Governance &amp; Administration
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Control Access. Reduce Risk. Stay Compliant.
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS IAM automates the entire identity lifecycle—from joiner to leaver—with
                access certification campaigns, role-based controls, and compliance reporting
                that auditors love.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {[
                  { value: 'All', label: 'Identities' },
                  { value: 'All', label: 'People' },
                  { value: '12', label: 'IAM Modules' },
                  { value: '4', label: 'Identity Sources' },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div style={{ color: BOSSS_BLUE }} className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-xs text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: BOSSS_BLUE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: BOSSS_BLUE, color: BOSSS_BLUE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Us
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Access Certification Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${BOSSS_BLUE}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <BadgeCheck className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Access Certification</h3>
                      <p className="text-xs text-gray-500">Campaign overview</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {['Overview', 'Campaigns', 'My Tasks'].map((tab, i) => (
                      <span
                        key={i}
                        style={i === 0 ? { backgroundColor: '#f59e0b' } : {}}
                        className={`px-3 py-1 rounded text-xs font-medium ${i === 0 ? 'text-white' : 'text-gray-600 bg-gray-100'}`}
                      >
                        {tab}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Active Campaigns', value: '3', icon: TrendingUp, color: '#3b82f6' },
                    { label: 'Pending Reviews', value: '12', icon: Clock, color: '#f59e0b' },
                    { label: 'Total Users', value: 'All', icon: Users, color: BOSSS_BLUE },
                    { label: 'High Risk Items', value: '2', icon: CircleAlert, color: '#ef4444' },
                  ].map((metric, i) => (
                    <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                      <metric.icon style={{ color: metric.color }} className="w-4 h-4 mx-auto mb-1" />
                      <p className="text-lg font-bold text-gray-900">{metric.value}</p>
                      <p className="text-xs text-gray-500">{metric.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: UserPlus, label: 'Create Campaign', desc: 'Start new review' },
                    { icon: ListChecks, label: 'Review Tasks', desc: 'View pending items' },
                    { icon: BarChart3, label: 'View Reports', desc: 'Access insights' },
                    { icon: Settings, label: 'Settings', desc: 'Configure options' },
                  ].map((action, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:shadow-md transition-all cursor-pointer">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100">
                        <action.icon className="w-4 h-4 text-gray-600" />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-900">{action.label}</p>
                        <p className="text-xs text-gray-500">{action.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/iam" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Identity Governance Challenges We Solve
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Manual identity management creates security risks and compliance gaps.
              Here&apos;s how BOSSS IAM transforms your access governance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-gray-600 text-sm">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${BOSSS_BLUE}08` }} className="flex items-start gap-4 p-4 rounded-lg">
                      <div style={{ backgroundColor: `${BOSSS_BLUE}20` }} className="w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                        <CheckCircle className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <p style={{ color: BOSSS_BLUE }} className="text-sm font-medium">BOSSS Solution</p>
                        <p className="text-sm text-gray-700 mt-1">{point.solution}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-linear-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete identity governance with access reviews, certification campaigns, and lifecycle management.
            </p>
          </motion.div>

          {/* Screenshot Tabs */}
          <ScreenshotGallery
            urlPath="iam"
            accentColor={BOSSS_BLUE}
            screenshots={[
              { src: iamPeopleEntitlement, label: 'Entitlements', alt: 'BOSSS IAM entitlement view showing who has access to what and how it was granted' },
              { src: iamPeople, label: 'People', alt: 'BOSSS IAM people directory linking identities to their accounts and teams' },
              { src: iamEntitlements, label: 'Entitlement Catalog', alt: 'BOSSS IAM entitlement catalog across connected source systems' },
              { src: iamCertifications, label: 'Certifications', alt: 'BOSSS IAM access certification campaigns and reviews' },
              { src: iamCertificationTasks, label: 'Review Tasks', alt: 'BOSSS IAM access review task queue with entitlement, campaign, reviewer and last-used date including never-used access' },
              { src: iamCertificationStages, label: 'Campaign Stages', alt: 'BOSSS IAM multi-stage access review campaign showing manager and resource-owner stages with approved and revoked rates' },
              { src: iamRequests, label: 'Access Requests', alt: 'BOSSS IAM access request queue with approval workflow' },
              { src: iamRbac, label: 'RBAC', alt: 'BOSSS IAM role-based access control definitions and role assignments' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Users, label: 'Identity Management', value: 'Centralized' },
              { icon: Key, label: 'Access Reviews', value: 'Automated' },
              { icon: ShieldCheck, label: 'Certification', value: 'Multi-stage' },
              { icon: RefreshCw, label: 'Lifecycle Sync', value: 'Real-time' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: BOSSS_BLUE }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Access Certification Campaigns Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div
                style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4"
              >
                <BadgeCheck className="w-4 h-4 mr-2" />
                Access Certification
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Automated Access Reviews That Actually Get Done
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Create certification campaigns that review access by entity, assets, or people.
                Multi-stage review workflows ensure thorough validation with manager and owner approvals.
              </p>

              {/* Campaign Wizard Steps */}
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {['Scope Type', 'Scope Selection', 'Review Stages', 'Advanced Settings', 'Schedule & Summary'].map((step, i) => (
                  <div key={i} className="flex items-center">
                    <div
                      style={i === 0 ? { backgroundColor: '#f59e0b' } : {}}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${i === 0 ? 'text-white' : 'text-gray-500 bg-gray-200'}`}
                    >
                      {i + 1}
                    </div>

                    <span className="text-xs text-gray-600 ml-1 mr-2">{step}</span>
                    {i < 4 && <ArrowRight className="w-3 h-3 text-gray-300" />}
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {[
                  'Multi-stage review with manager and owner approvals',
                  'Automatic reminder escalations for pending reviews',
                  'Risk-based prioritization of high-risk entitlements',
                  'Bulk approve/revoke with audit trail',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 shrink-0" style={{ color: BOSSS_BLUE }} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Campaign Scope Types */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Select Scope Type</h3>
                <p className="text-sm text-gray-500 mb-6">Choose how you want to define the scope of this access certification campaign.</p>

                <div className="space-y-4">
                  {campaignScopes.map((scope, i) => (
                    <motion.div
                      key={i}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${i === 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100">
                          <scope.icon className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900">{scope.name}</h4>
                          <p className="text-sm text-gray-500">{scope.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 12 IAM Capabilities Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Identity Governance Platform
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              12 integrated modules for comprehensive identity lifecycle management,
              access governance, and compliance automation.
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-12 overflow-x-auto px-4 -mx-4 sm:mx-0 sm:px-0">
            <div className="inline-flex bg-white rounded-xl p-1 shadow-md border border-gray-200 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  style={{
                    backgroundColor: activeTab === tab.id ? BOSSS_BLUE : 'transparent',
                  }}
                  className={`flex items-center gap-2 px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}

                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Capabilities Grid */}
          <motion.div
            key={activeTab}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            initial={{ opacity: 0, y: 20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {capabilityGroups[activeTab].map((capability, index) => (
              <motion.div
                key={capability.name}
                animate={{ opacity: 1, scale: 1 }}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-200 group">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div
                        style={{ backgroundColor: `${BOSSS_BLUE}12` }}
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                      >
                        <capability.icon className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                          {capability.name}
                        </h4>

                        <p className="text-sm text-gray-600 leading-relaxed">
                          {capability.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Identity Sources & People Management */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Identity Sources */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Multi-Source Identity Sync
              </h3>

              <p className="text-gray-600 mb-6">
                Automatically sync identities from Active Directory, Okta, Salesforce, Entra ID,
                and more. Track Human and Non-Human identities in a unified view.
              </p>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Fingerprint className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                    <span className="font-semibold text-gray-900">Identities</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">All Sources</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">Human</span>
                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">Non Human</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {identitySources.map((source, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div
                          style={{ backgroundColor: `${source.color}15` }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                        >
                          <Database className="w-4 h-4" style={{ color: source.color }} />
                        </div>

                        <span className="font-medium text-gray-900">{source.name}</span>
                      </div>

                      <span className="text-sm text-gray-600">
                        {source.identities}
                        {' '}
                        identities
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button className="w-full py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Sync Identities (Azure Cloud)
                  </button>
                </div>
              </div>
            </motion.div>

            {/* People Lifecycle */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                People Lifecycle Management
              </h3>

              <p className="text-gray-600 mb-6">
                Track every person in your organization through their entire lifecycle—from
                onboarding to offboarding—with complete status visibility.
              </p>

              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: BOSSS_BLUE }} />
                    <span className="font-semibold text-gray-900">People</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">All Personnel</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">Internal</span>
                    <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">External</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {peopleStatuses.slice(0, 3).map((status, i) => (
                    <div key={i} className="text-center p-3 rounded-lg bg-gray-50">
                      <p className="text-xl font-bold" style={{ color: status.color }}>{status.count}</p>
                      <p className="text-xs text-gray-500">{status.name}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {peopleStatuses.slice(3).map((status, i) => (
                    <div key={i} className="text-center p-3 rounded-lg bg-gray-50">
                      <p className="text-xl font-bold" style={{ color: status.color }}>{status.count}</p>
                      <p className="text-xs text-gray-500">{status.name}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                  <button className="flex-1 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-all">
                    Import
                  </button>

                  <button className="flex-1 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-all">
                    Export
                  </button>

                  <button style={{ backgroundColor: BOSSS_BLUE }} className="flex-1 py-2 text-sm font-medium text-white rounded-lg transition-all">
                    + Add People
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RBAC & SOD Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Access Controls
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Role-based access control with NIST NICE framework alignment and
              segregation of duties enforcement.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* RBAC Card */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${BOSSS_BLUE}15` }} className="w-12 h-12 rounded-lg flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <div>
                      <CardTitle>Role-Based Access Control</CardTitle>
                      <p className="text-sm text-gray-500">NICE Framework aligned roles</p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Access Type', values: ['Privileged (P)', 'Non-Privileged (NP)', 'No Logical Access (NLA)'] },
                      { label: 'Sensitivity Level', values: ['High', 'Medium', 'Low', 'No Sensitivity'] },
                      { label: 'NICE Role', values: ['Systems Security Management', 'Incident Response', 'Security Architecture'] },
                    ].map((field, i) => (
                      <div key={i} className="p-3 rounded-lg bg-gray-50">
                        <p className="text-sm font-medium text-gray-700 mb-2">{field.label}</p>

                        <div className="flex flex-wrap gap-1">
                          {field.values.map((val, j) => (
                            <span key={j} className="px-2 py-0.5 rounded text-xs bg-white border border-gray-200 text-gray-600">
                              {val}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* SOD Card */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: '#ef444415' }} className="w-12 h-12 rounded-lg flex items-center justify-center">
                      <Grid3X3 className="w-6 h-6" style={{ color: '#ef4444' }} />
                    </div>

                    <div>
                      <CardTitle>Segregation of Duties Matrix</CardTitle>
                      <p className="text-sm text-gray-500">Conflict detection & prevention</p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border-2 border-red-100 bg-red-50">
                      <div className="flex items-center gap-2 mb-2">
                        <CircleAlert className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-700">SOD Violation Detected</span>
                      </div>

                      <p className="text-sm text-red-600">
                        User has conflicting access: &quot;Create Purchase Orders&quot; and &quot;Approve Payments&quot;
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Conflicts Detected', value: '3', color: '#ef4444' },
                        { label: 'Rules Defined', value: '24', color: '#10b981' },
                        { label: 'Mitigating Controls', value: '2', color: '#f59e0b' },
                        { label: 'Clean Users', value: '482', color: BOSSS_BLUE },
                      ].map((stat, i) => (
                        <div key={i} className="text-center p-3 rounded-lg bg-gray-50">
                          <p className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                          <p className="text-xs text-gray-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Use Cases */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Compliance-Ready for Every Industry
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS IGA helps you meet regulatory requirements with built-in controls and audit-ready reports.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Building2,
                title: 'Financial Services',
                frameworks: ['SOX', 'PCI DSS', 'GLBA'],
                benefits: ['SOX 404 access controls', 'Segregation of duties', 'Access certification'],
              },
              {
                icon: Heart,
                title: 'Healthcare',
                frameworks: ['HIPAA', 'HITECH'],
                benefits: ['PHI access controls', 'Role-based permissions', 'Audit trail logging'],
              },
              {
                icon: Lock,
                title: 'Government',
                frameworks: ['FedRAMP', 'FISMA', 'CMMC'],
                benefits: ['Clearance management', 'Need-to-know access', 'Continuous monitoring'],
              },
              {
                icon: Cloud,
                title: 'Technology',
                frameworks: ['SOC 2', 'ISO 27001'],
                benefits: ['Privileged access mgmt', 'Service account control', 'API access governance'],
              },
            ].map((industry, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-4">
                    <div
                      style={{ backgroundColor: `${BOSSS_BLUE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <industry.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                      {industry.title}
                    </CardTitle>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {industry.frameworks.map((fw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                          {fw}
                        </span>
                      ))}
                    </div>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-2">
                      {industry.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-600">
                          <CheckCircle style={{ color: BOSSS_BLUE }} className="w-4 h-4 mr-2 mt-0.5 shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={12}
        agentName="IAM Agent"
        accentColor="#10b981"
        description="Your AI-powered identity governance copilot. Ask about access reviews, entitlements, or certification campaigns—get instant answers."
        exampleQueries={[
          '"Who has admin access to production systems?"',
          '"Show me users with elevated privileges"',
          '"List pending access requests"',
          '"Which certification campaigns are overdue?"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: BOSSS_BLUE }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Automate Identity Governance?
            </h2>

            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS IGA can eliminate access sprawl, automate certifications,
              and keep you audit-ready year-round.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-linear-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule Your Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Contact Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
